import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AIInsightPanel = ({ onGenerateReport = () => {} }) => {
  const [expandedInsight, setExpandedInsight] = useState(null);

  const insights = [
    {
      id: 1,
      type: 'success',
      icon: 'TrendingUp',
      title: 'Strong Revenue Growth Detected',
      summary: 'Your revenue has increased by 8% this month, exceeding the target by 5.6%.',
      details: `Analysis shows consistent growth across all product lines with particularly strong performance in Q4. Customer acquisition costs have decreased by 12% while retention rates improved to 94%. This indicates healthy business fundamentals and effective marketing strategies.\n\nRecommendation: Consider scaling your top-performing channels and allocating additional budget to customer retention programs.`,
      priority: 'high'
    },
    {
      id: 2,
      type: 'warning',
      icon: 'AlertTriangle',
      title: 'Cost Structure Optimization Needed',
      summary: 'Operating costs have increased by 15% in the last quarter.',
      details: `Detailed breakdown reveals that fixed costs remain stable, but variable costs have risen significantly due to increased supplier prices and logistics expenses. This trend could impact profit margins if not addressed.\n\nRecommendation: Review supplier contracts, explore bulk purchasing options, and consider alternative logistics partners to reduce variable costs by an estimated 8-10%.`,
      priority: 'medium'
    },
    {
      id: 3,
      type: 'info',
      icon: 'Lightbulb',
      title: 'Market Expansion Opportunity',
      summary: 'Data suggests potential for geographic expansion based on customer demand patterns.',
      details: `Customer inquiry analysis shows 23% of leads originate from regions where you currently have no presence. Market research indicates these areas have favorable demographics and lower competition.\n\nRecommendation: Conduct feasibility study for expansion into top 3 identified regions. Estimated investment: ₹50,000-75,000 with projected ROI of 180% within 18 months.`,
      priority: 'low'
    }
  ];

  const getInsightStyles = (type) => {
    const styles = {
      success: {
        bg: 'bg-success/10',
        border: 'border-success/20',
        icon: 'var(--color-success)',
        text: 'text-success'
      },
      warning: {
        bg: 'bg-warning/10',
        border: 'border-warning/20',
        icon: 'var(--color-warning)',
        text: 'text-warning'
      },
      info: {
        bg: 'bg-accent/10',
        border: 'border-accent/20',
        icon: 'var(--color-accent)',
        text: 'text-accent'
      }
    };
    return styles?.[type] || styles?.info;
  };

  const toggleInsight = (id) => {
    setExpandedInsight(expandedInsight === id ? null : id);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 elevation-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="Sparkles" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="text-lg md:text-xl font-semibold text-foreground font-heading">
              AI-Powered Insights
            </h3>
            <p className="text-sm text-muted-foreground font-caption mt-1">
              Personalized recommendations based on your business data
            </p>
          </div>
        </div>
      </div>
      <div className="space-y-4 mb-6">
        {insights?.map((insight) => {
          const styles = getInsightStyles(insight?.type);
          const isExpanded = expandedInsight === insight?.id;

          return (
            <div
              key={insight?.id}
              className={`border rounded-lg p-4 transition-smooth ${styles?.bg} ${styles?.border}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-md ${styles?.bg} flex items-center justify-center flex-shrink-0`}>
                  <Icon name={insight?.icon} size={16} color={styles?.icon} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h4 className={`text-sm md:text-base font-semibold font-body ${styles?.text}`}>
                      {insight?.title}
                    </h4>
                    <button
                      onClick={() => toggleInsight(insight?.id)}
                      className="p-1 hover:bg-background/50 rounded transition-smooth flex-shrink-0"
                      aria-label={isExpanded ? 'Collapse insight' : 'Expand insight'}
                    >
                      <Icon 
                        name={isExpanded ? 'ChevronUp' : 'ChevronDown'} 
                        size={16} 
                        color="var(--color-muted-foreground)" 
                      />
                    </button>
                  </div>
                  <p className="text-sm text-foreground font-body mb-2">
                    {insight?.summary}
                  </p>
                  {isExpanded && (
                    <div className="mt-3 pt-3 border-t border-border/50">
                      <p className="text-sm text-muted-foreground font-body whitespace-pre-line">
                        {insight?.details}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <Button
        variant="default"
        size="lg"
        fullWidth
        iconName="FileText"
        iconPosition="left"
        onClick={onGenerateReport}
      >
        Generate Comprehensive Decision Report
      </Button>
      <p className="text-xs text-center text-muted-foreground font-caption mt-3">
        AI insights are updated daily based on your latest business data
      </p>
    </div>
  );
};

export default AIInsightPanel;